import { createFileRoute, Link, useNavigate, Outlet } from "@tanstack/react-router";
import type { OutputLanguage } from "@alchema/shared";
import * as React from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { requireAuth } from "~/lib/auth-guard";
import { orpc } from "~/lib/orpc-client";
import { queryClient } from "~/lib/query-client";
import { Skeleton, useToast } from "@alchema/ui";
import { AiActionButton } from "~/components/AiActionButton";
import { AiProcessingNotice } from "~/components/AiProcessingNotice";
import { ConfirmDialog } from "~/components/ConfirmDialog";
import { EmptyState } from "~/components/shared/EmptyState";
import type { Tier } from "~/lib/tier";
import * as m from "~/paraglide/messages";
import { OutputLanguageSelect } from "~/components/OutputLanguageSelect";

export const Route = createFileRoute("/cover-letters")({
  beforeLoad: async () => { await requireAuth(); },
  loader: async () => {
    await Promise.all([
      queryClient.prefetchQuery(orpc.coverLetter.list.queryOptions({ input: undefined })),
      queryClient.prefetchQuery(orpc.job.list.queryOptions({ input: undefined })),
      queryClient.prefetchQuery(orpc.resume.list.queryOptions({ input: undefined })),
    ]);
    return null;
  },
  component: CoverLettersLayout,
  pendingComponent: CoverLettersSkeleton,
});

function CoverLettersSkeleton() {
  return (
    <div className="animate-fade-up">
      <div className="flex items-center justify-between pb-6 border-b border-[var(--border-subtle)] mb-6">
        <div>
          <Skeleton className="h-[22px] w-40" />
          <Skeleton className="mt-1.5 h-4 w-24" />
        </div>
        <Skeleton className="h-9 w-36 rounded-[8px]" />
      </div>
      <div className="rounded-[8px] border border-[var(--border-subtle)] overflow-hidden">
        {[1, 2, 3].map((i) => (
          <div key={i} className="flex items-center gap-4 px-4 py-3 border-b border-[var(--border-subtle)] last:border-b-0">
            <div className="flex-1">
              <Skeleton className="h-[13px] w-48" />
              <Skeleton className="mt-1 h-[11px] w-32" />
            </div>
            <Skeleton className="h-5 w-20 rounded-full" />
            <Skeleton className="h-4 w-16" />
          </div>
        ))}
      </div>
    </div>
  );
}

function CoverLettersLayout() {
  return (
    <>
      <CoverLettersPage />
      <Outlet />
    </>
  );
}

export function CoverLetterGenerateAiNotice() {
  return (
    <AiProcessingNotice
      id="cover-letter-generate-ai-processing-notice"
      variant="compact"
      className="mt-3"
    />
  );
}

/* Tone dot colors — light indicator instead of heavy badge */
const TONE_LABELS: Record<string, { labelKey: () => string; dotClass: string }> = {
  formal: { labelKey: () => m.cover_letters_tone_formal(), dotClass: "bg-[var(--accent)]" },
  conversational: { labelKey: () => m.cover_letters_tone_conversational(), dotClass: "bg-[var(--success,#34d399)]" },
  enthusiastic: { labelKey: () => m.cover_letters_tone_enthusiastic(), dotClass: "bg-[var(--warning,#fbbf24)]" },
};

/* Relative time helper */
function relativeTime(date: string | Date): string {
  const now = Date.now();
  const then = new Date(date).getTime();
  const diff = Math.floor((now - then) / 1000);
  if (diff < 60) return "just now";
  if (diff < 3600) return `${Math.floor(diff / 60)}m ago`;
  if (diff < 86400) return `${Math.floor(diff / 3600)}h ago`;
  if (diff < 2592000) return `${Math.floor(diff / 86400)}d ago`;
  return new Date(date).toLocaleDateString("en-GB", { day: "numeric", month: "short" });
}

function CoverLettersPage() {
  const { toast } = useToast();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [showGenerate, setShowGenerate] = React.useState(false);
  const [selectedJobId, setSelectedJobId] = React.useState("");
  const [selectedResumeId, setSelectedResumeId] = React.useState("");
  const [selectedTone, setSelectedTone] = React.useState<"formal" | "conversational" | "enthusiastic">("formal");
  const [deleteTarget, setDeleteTarget] = React.useState<string | null>(null);
  const [selectedOutputLanguage, setSelectedOutputLanguage] = React.useState("en");
  const [hoveredRow, setHoveredRow] = React.useState<string | null>(null);

  const creditQuery = useQuery(orpc.credits.status.queryOptions({ input: undefined }));
  const credits = creditQuery.data;

  const coverLettersQuery = useQuery(orpc.coverLetter.list.queryOptions({ input: undefined }));
  const jobsQuery = useQuery(orpc.job.list.queryOptions({ input: undefined }));
  const resumesQuery = useQuery(orpc.resume.list.queryOptions({ input: undefined }));

  const generateMutation = useMutation(orpc.coverLetter.generate.mutationOptions({
    onSuccess: (data) => {
      console.log("[cover-letter] Generated:", data.id);
      queryClient.invalidateQueries({ queryKey: orpc.coverLetter.list.key({}) });
      setShowGenerate(false);
      toast(m.cover_letters_toast_generated(), "success");
      navigate({ to: "/cover-letters/$coverId", params: { coverId: data.id } });
    },
    onError: (err) => {
      console.error("[cover-letter] Generate error:", err);
      toast(err.message || m.cover_letters_toast_generate_error(), "error");
    },
  }));

  const deleteMutation = useMutation(orpc.coverLetter.delete.mutationOptions({
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: orpc.coverLetter.list.key({}) });
      toast(m.cover_letters_toast_deleted(), "success");
    },
  }));

  const coverLetters = coverLettersQuery.data ?? [];
  const jobs = jobsQuery.data ?? [];
  const resumes = resumesQuery.data ?? [];

  const selectedJob = jobs.find((j: { id: string }) => j.id === selectedJobId);
  const detectedLang = (selectedJob?.parsedRequirements as Record<string, unknown> | null)?.detectedLanguage as string | undefined;

  React.useEffect(() => {
    if (detectedLang) setSelectedOutputLanguage(detectedLang);
  }, [selectedJobId, detectedLang]);

  return (
    <div className="animate-fade-up">
      {/* Page header — Attio editorial style */}
      <div className="flex items-start justify-between pb-6 border-b border-[var(--border-subtle)] mb-6">
        <div>
          <h1
            className="font-display text-[22px] font-semibold text-[var(--text)] leading-tight"
            style={{ letterSpacing: "-0.5px" }}
          >
            {m.cover_letters_title()}
          </h1>
          <p className="mt-1 text-[13px] text-[var(--text-muted)]">
            {m.cover_letters_count({ count: coverLetters.length, plural: coverLetters.length !== 1 ? "s" : "" })}
          </p>
        </div>
        <button
          onClick={() => setShowGenerate(!showGenerate)}
          className="btn btn-primary btn-sm"
          data-testid="cover-letter-generate-btn"
        >
          {showGenerate ? m.cover_letters_close() : m.cover_letters_generate_cta()}
        </button>
      </div>

      {/* Generate form — whisper-border card */}
      {showGenerate && (
        <div
          className="rounded-[8px] border border-[var(--border-subtle)] bg-[var(--surface)] p-6 mb-6 animate-fade-up stagger-1"
        >
          <h3 className="text-[13px] font-semibold text-[var(--text)]">{m.cover_letters_generate_title()}</h3>
          <p className="mt-1 text-[12px] text-[var(--text-muted)]">{m.cover_letters_generate_subtitle()}</p>
          <CoverLetterGenerateAiNotice />

          <div className="mt-4 grid grid-cols-1 gap-4 md:grid-cols-2">
            <div>
              <label htmlFor="cl-job-select" className="block text-[12px] font-medium text-[var(--text-secondary)]">{m.cover_letters_label_job()}</label>
              <select
                id="cl-job-select"
                value={selectedJobId}
                onChange={(e) => setSelectedJobId(e.target.value)}
                className="input mt-1.5 w-full min-h-[44px]"
              >
                <option value="">{m.cover_letters_select_job()}</option>
                {jobs.map((j) => (
                  <option key={j.id} value={j.id}>
                    {m.cover_letters_job_at_company({ title: j.title, company: j.company })}
                  </option>
                ))}
              </select>
              {jobs.length === 0 && (
                <p className="mt-1.5 text-[11px] text-[var(--text-muted)]">
                  {m.cover_letters_prereq_job()}{" "}
                  <Link to="/jobs" className="text-[var(--accent)] hover:underline">{m.cover_letters_prereq_job_link()}</Link>
                </p>
              )}
            </div>
            <div>
              <label htmlFor="cl-resume-select" className="block text-[12px] font-medium text-[var(--text-secondary)]">{m.cover_letters_label_resume()}</label>
              <select
                id="cl-resume-select"
                value={selectedResumeId}
                onChange={(e) => setSelectedResumeId(e.target.value)}
                className="input mt-1.5 w-full min-h-[44px]"
                disabled={resumes.length === 0}
              >
                <option value="">{m.cover_letters_select_resume()}</option>
                {resumes.map((r) => (
                  <option key={r.id} value={r.id}>
                    {r.isMaster ? m.cover_letters_resume_master({ title: r.title }) : r.title}
                  </option>
                ))}
              </select>
              {resumes.length === 0 && (
                <p className="mt-1.5 text-[11px] text-[var(--text-muted)]">
                  {m.cover_letters_prereq_resume()}{" "}
                  <Link to="/resumes" className="text-[var(--accent)] hover:underline">{m.cover_letters_prereq_resume_link()}</Link>
                </p>
              )}
            </div>
          </div>

          <fieldset className="mt-4 border-0 p-0 m-0">
            <legend className="block text-[12px] font-medium text-[var(--text-secondary)]">{m.cover_letters_label_tone()}</legend>
            <div className="mt-2 flex gap-2 flex-wrap">
              {(["formal", "conversational", "enthusiastic"] as const).map((tone) => (
                <label
                  key={tone}
                  className={`flex cursor-pointer items-center gap-1.5 rounded-[8px] border px-3 py-2 min-h-[36px] text-[12px] font-medium transition-all duration-200 ${
                    selectedTone === tone
                      ? "border-[var(--accent)] bg-[var(--accent)] text-white"
                      : "border-[var(--border-subtle)] bg-[var(--surface-raised)] text-[var(--text-secondary)] hover:border-[var(--border)] hover:bg-[var(--surface)]"
                  }`}
                >
                  <input
                    type="radio"
                    name="tone"
                    value={tone}
                    checked={selectedTone === tone}
                    onChange={() => setSelectedTone(tone)}
                    className="sr-only"
                  />
                  <span
                    className={`h-1.5 w-1.5 rounded-full flex-shrink-0 ${
                      selectedTone === tone ? "bg-white" : TONE_LABELS[tone].dotClass
                    }`}
                  />
                  {TONE_LABELS[tone].labelKey()}
                </label>
              ))}
            </div>
          </fieldset>

          <OutputLanguageSelect
            value={selectedOutputLanguage}
            onChange={setSelectedOutputLanguage}
            detectedLanguage={detectedLang}
            className="mt-4"
          />

          <div className="mt-5 flex gap-2 pt-4 border-t border-[var(--border-subtle)]">
            <AiActionButton
              creditCost={1}
              onClick={() => {
                console.log("[cover-letter] Generate clicked", { selectedJobId, selectedResumeId, selectedTone });
                if (!selectedJobId || !selectedResumeId) {
                  toast(m.cover_letters_select_both(), "error");
                  return;
                }
                generateMutation.mutate({
                  jobId: selectedJobId,
                  resumeId: selectedResumeId,
                  tone: selectedTone,
                  outputLanguage: selectedOutputLanguage as OutputLanguage,
                });
              }}
              disabled={generateMutation.isPending}
              creditsRemaining={credits?.totalAvailable ?? credits?.balance ?? 0}
              creditLimit={credits?.limit ?? 0}
              creditResetAt={credits?.resetAt ? new Date(credits.resetAt) : new Date()}
              tier={(credits?.tier as Tier) ?? "free"}
              className="btn btn-primary btn-sm"
            >
              {generateMutation.isPending ? m.cover_letters_generating() : m.cover_letters_generate()}
            </AiActionButton>
            <button
              onClick={() => setShowGenerate(false)}
              className="btn btn-ghost btn-sm"
            >
              {m.cover_letters_cancel()}
            </button>
          </div>
        </div>
      )}

      {/* List */}
      {coverLettersQuery.isLoading ? (
        <CoverLettersSkeleton />
      ) : coverLetters.length === 0 ? (
        <div className="flex flex-col items-center justify-center py-16 text-center">
          <div style={{ color: "var(--text-muted)", opacity: 0.5 }} className="mb-4">
            <svg width="48" height="48" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={1.5}>
              <path d="M4 4h16c1.1 0 2 .9 2 2v12c0 1.1-.9 2-2 2H4c-1.1 0-2-.9-2-2V6c0-1.1.9-2 2-2z" />
              <polyline points="22,6 12,13 2,6" />
            </svg>
          </div>
          <p className="text-sm font-medium" style={{ color: "var(--text-secondary)" }}>{m.cover_letters_empty_title()}</p>
          <p className="mt-1 text-xs max-w-xs" style={{ color: "var(--text-muted)" }}>{m.cover_letters_empty_desc()}</p>
          <button onClick={() => setShowGenerate(true)} className="btn btn-primary btn-sm mt-5">
            {m.cover_letters_generate_cta()}
          </button>
        </div>
      ) : (
        <div
          className="rounded-[8px] border border-[var(--border-subtle)] overflow-hidden"
          data-testid="cover-letter-list"
        >
          {/* Table header */}
          <div className="grid grid-cols-[1fr_auto_auto_auto] items-center px-4 py-2 border-b border-[var(--border-subtle)] bg-[var(--surface-raised)]">
            <span className="text-[11px] font-medium text-[var(--text-muted)] uppercase tracking-wide">
              Title / Job
            </span>
            <span className="text-[11px] font-medium text-[var(--text-muted)] uppercase tracking-wide w-28 text-left">
              Tone
            </span>
            <span className="text-[11px] font-medium text-[var(--text-muted)] uppercase tracking-wide w-24 text-left">
              Created
            </span>
            <span className="w-16" />
          </div>

          {/* Table rows */}
          {coverLetters.map((cl) => {
            const toneInfo = TONE_LABELS[cl.tone] ?? TONE_LABELS.formal;
            const isHovered = hoveredRow === cl.id;

            return (
              <div
                key={cl.id}
                className="grid grid-cols-[1fr_auto_auto_auto] items-center px-4 py-3 border-b border-[var(--border-subtle)] last:border-b-0 transition-colors duration-150 hover:bg-[var(--surface-raised)]"
                onMouseEnter={() => setHoveredRow(cl.id)}
                onMouseLeave={() => setHoveredRow(null)}
                data-testid="cover-letter-card"
              >
                {/* Title + company */}
                <div className="min-w-0 pr-4">
                  <Link
                    to="/cover-letters/$coverId"
                    params={{ coverId: cl.id }}
                    className="block text-[13px] font-medium text-[var(--text)] hover:text-[var(--accent)] transition-colors"
                  >
                    {cl.title}
                  </Link>
                  <p className="mt-0.5 text-[11px] text-[var(--text-muted)]">
                    {cl.jobTitle && cl.jobCompany
                      ? m.cover_letters_job_at_company({ title: cl.jobTitle, company: cl.jobCompany })
                      : m.cover_letters_no_associated_job()}
                  </p>
                </div>

                {/* Tone — dot + text */}
                <div className="w-28 flex items-center gap-1.5">
                  <span className={`h-1.5 w-1.5 rounded-full flex-shrink-0 ${toneInfo.dotClass}`} />
                  <span className="text-[12px] text-[var(--text-secondary)]">{toneInfo.labelKey()}</span>
                </div>

                {/* Created */}
                <div className="w-24">
                  <span className="text-[12px] text-[var(--text-muted)]">
                    {relativeTime(cl.createdAt)}
                  </span>
                </div>

                {/* Actions — visible on hover */}
                <div className={`w-16 flex items-center justify-end gap-1 transition-opacity duration-150 ${isHovered ? "opacity-100" : "opacity-0"}`}>
                  <Link
                    to="/cover-letters/$coverId"
                    params={{ coverId: cl.id }}
                    className="rounded-[6px] p-1.5 text-[var(--text-muted)] hover:bg-[var(--accent-subtle,rgba(38,109,240,0.08))] hover:text-[var(--accent)] transition-colors"
                    aria-label={m.cover_letters_edit()}
                    title={m.cover_letters_edit()}
                  >
                    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2} strokeLinecap="round" strokeLinejoin="round">
                      <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z" />
                      <circle cx="12" cy="12" r="3" />
                    </svg>
                  </Link>
                  <button
                    onClick={() => setDeleteTarget(cl.id)}
                    className="rounded-[6px] p-1.5 text-[var(--text-muted)] hover:bg-[var(--error-subtle)] hover:text-[var(--error)] transition-colors"
                    aria-label={m.cover_letters_delete()}
                    title={m.cover_letters_delete()}
                    data-testid="cover-letter-delete-btn"
                  >
                    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2} strokeLinecap="round" strokeLinejoin="round">
                      <polyline points="3 6 5 6 21 6" />
                      <path d="M19 6v14a2 2 0 01-2 2H7a2 2 0 01-2-2V6m3 0V4a2 2 0 012-2h4a2 2 0 012 2v2" />
                    </svg>
                  </button>
                </div>
              </div>
            );
          })}
        </div>
      )}

      <ConfirmDialog
        open={deleteTarget !== null}
        onConfirm={() => {
          if (deleteTarget) deleteMutation.mutate({ id: deleteTarget });
          setDeleteTarget(null);
        }}
        onCancel={() => setDeleteTarget(null)}
        title={m.cover_letters_delete_confirm()}
        confirmLabel={m.cover_letters_delete()}
        cancelLabel={m.cover_letters_cancel()}
        destructive
      />
    </div>
  );
}
